import React, { useEffect, useRef, useState } from 'react';
import {
    Box, TextField, Button, Card, CardContent, Typography, Select, MenuItem, FormControl,
    InputLabel, Checkbox, FormControlLabel, Alert, Grid
} from '@mui/material';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import moment from 'moment';
import { serviceHTTP } from '../../util/service';
import { useSelector } from 'react-redux';
import { getAccessToken, getPermissionByName } from '../../util/customMethods';
import { notify } from '../../util/notifications';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from '../../util/googleMaps';

const emptyPickup = { addressLine: '', city: '', state: '', zip: '', landmark: '', latLong: '' };
const emptyDetails = {
    licensePlate: '', vehicleMake: '', vehicleModel: '', vehicleColor: '', reason: '', contactName: '', contactPhone: '',
    noOneWithUnit: false, keysLocation: '', poNo: '', referenceNo: '', commercialUnit: '', defects: '', scheduled: false, scheduleType: 'ARRIVE_AT', scheduleAt: '',
};
const filled = (value) => !!(value && value.trim());

const addressPart = (components, type, useShortName = false) => {
    const part = (components || []).find(c => c.types.includes(type));
    if (!part) return '';
    return useShortName ? part.short_name : part.long_name;
};

const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
});

const NewServiceRequest = ({ history }) => {
    const refNotify = useSelector((state) => state.refNotify);
    const [accounts, setAccounts] = useState([]);
    const [accountsLoaded, setAccountsLoaded] = useState(false);
    const [accountId, setAccountId] = useState('');
    const [pickup, setPickup] = useState(emptyPickup);
    const [vehicles, setVehicles] = useState(1);
    const [trucks, setTrucks] = useState(1);
    const [flatbed, setFlatbed] = useState(false);
    const [vin, setVin] = useState('');
    const [note, setNote] = useState('');
    const [photos, setPhotos] = useState([]);
    const [details, setDetails] = useState(emptyDetails);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [created, setCreated] = useState('');
    const autocompleteRef = useRef(null);
    const { isLoaded } = useJsApiLoader({ id: 'act-google-maps', googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBRARIES });

    useEffect(() => {
        serviceHTTP.get('serviceRequest/accounts', undefined, getAccessToken())
            .then(response => {
                const list = Array.isArray(response) ? response : [];
                setAccounts(list);
                if (list.length === 1) setAccountId(list[0].accountId);
            })
            .catch(() => setAccounts([]))
            .finally(() => setAccountsLoaded(true));
    }, []);

    const account = accounts.find(a => a.accountId === accountId) || {};
    const isPrivateProperty = account.arsType === 'PRIVATE_PROPERTY';
    const isCommercial = account.arsType === 'COMMERCIAL';
    const contactRequired = account.contactRequired === 1;
    const setDetail = (key, value) => setDetails(prev => ({ ...prev, [key]: value }));

    const typeError = () => {
        const d = details;
        if (isPrivateProperty) {
            if (!filled(d.licensePlate) && !(filled(d.vehicleMake) && filled(d.vehicleModel) && filled(d.vehicleColor)))
                return 'Enter the plate, or the make, model and color.';
            if (!filled(d.reason)) return 'Select the violation.';
        }
        if (isCommercial) {
            if (d.noOneWithUnit ? !filled(d.keysLocation) : !(filled(d.contactName) && filled(d.contactPhone)))
                return 'Enter the person with the unit and their number, or check No one with unit and give the keys location.';
            if (!filled(d.commercialUnit)) return 'Enter the Unit #.';
        }
        if (contactRequired && !(isCommercial && d.noOneWithUnit) && !filled(d.contactPhone))
            return 'Contact # is required for this account.';
        if ((isPrivateProperty || isCommercial) && d.scheduled && !d.scheduleAt)
            return 'Enter the scheduled date and time.';
        return '';
    };

    const scheduleFields = () => details.scheduled && details.scheduleAt
        ? { scheduleType: details.scheduleType, scheduleDateTime: moment(details.scheduleAt).format('MM/DD/YYYY HH:mm') }
        : {};

    const typeFields = () => {
        const d = details;
        if (isPrivateProperty)
            return { licensePlate: d.licensePlate, vehicleMake: d.vehicleMake, vehicleModel: d.vehicleModel,
                vehicleColor: d.vehicleColor, reason: d.reason, contactName: d.contactName, contactPhone: d.contactPhone,
                ...scheduleFields() };
        if (isCommercial)
            return { contactName: d.noOneWithUnit ? '' : d.contactName, contactPhone: d.noOneWithUnit ? '' : d.contactPhone,
                noOneWithUnit: d.noOneWithUnit, keysLocation: d.noOneWithUnit ? d.keysLocation : '', poNo: d.poNo,
                referenceNo: d.referenceNo, commercialUnit: d.commercialUnit, defects: d.defects, licensePlate: d.licensePlate,
                ...scheduleFields() };
        return contactRequired ? { contactName: d.contactName, contactPhone: d.contactPhone } : {};
    };

    const onPlaceChanged = () => {
        const place = autocompleteRef.current && autocompleteRef.current.getPlace();
        if (!place || !place.geometry) return;
        const components = place.address_components;
        const street = [addressPart(components, 'street_number'), addressPart(components, 'route')]
            .filter(Boolean).join(' ');
        setPickup({
            ...pickup,
            addressLine: street || place.name || '',
            city: addressPart(components, 'locality') || addressPart(components, 'sublocality'),
            state: addressPart(components, 'administrative_area_level_1', true),
            zip: addressPart(components, 'postal_code'),
            latLong: `${place.geometry.location.lat()},${place.geometry.location.lng()}`,
        });
    };

    const resetForm = () => {
        setPickup(emptyPickup);
        setVehicles(1);
        setTrucks(1);
        setFlatbed(false);
        setVin('');
        setNote('');
        setPhotos([]);
        setDetails(emptyDetails);
    };

    const handleSubmit = async () => {
        setError('');
        setCreated('');
        if (!accountId) {
            setError('Select an account.');
            return;
        }
        if (!pickup.latLong) {
            setError('Pick the pickup address from the suggestions so the location can be mapped.');
            return;
        }
        const detailsError = typeError();
        if (detailsError) {
            setError(detailsError);
            return;
        }
        const vinValue = vin.trim().toUpperCase();
        if (vinValue && vinValue.length !== 17) {
            setError('VIN must be 17 characters, or leave it blank.');
            return;
        }
        setSubmitting(true);
        try {
            const now = moment();
            const body = {
                accountId,
                pickupAddrLine1: pickup.addressLine,
                pickupCity: pickup.city,
                pickupState: pickup.state,
                pickupZipcode: pickup.zip,
                pickupLandmark: pickup.landmark,
                pickupLatLong: pickup.latLong,
                pickupAddrVerified: 1,
                duplicateCopies: vehicles,
                noOfTrucks: trucks,
                flatbedNeeded: flatbed,
                VIN: vinValue ? [vinValue] : [],
                savedNote: note.trim(),
                pic: await Promise.all(photos.map(toBase64)),
                receiverDate: now.format('MM/DD/YYYY'),
                requestTime: now.format('MM/DD/YYYY HH:mm:ss'),
                receivedTime: now.format('MM/DD/YYYY HH:mm:ss'),
                ...typeFields(),
            };
            const response = await serviceHTTP.post('serviceRequest', body, undefined, getAccessToken());
            if (response.status === 200 && response.statusCode === 200) {
                const invoices = String(response.resp || '');
                resetForm();
                if (getPermissionByName('dashboard')) {
                    notify.createNotification('success',
                        `Request submitted. Invoice${invoices.includes(',') ? 's' : ''}: ${invoices}`, refNotify);
                    history.push('/dashboard');
                    return;
                }
                setCreated(invoices);
            } else {
                setError(response.message || 'Unable to submit the request. Please try again.');
            }
        } catch (e) {
            setError('Unable to submit the request. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (accountsLoaded && accounts.length === 0) {
        return (
            <Box>
                <Alert severity="info">None of your accounts are set up to take service requests. Contact All City Towing to enable it.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 900 }}>
            {created && (
                <Alert severity="success" sx={{ marginBottom: 2 }}>
                    Request submitted. Invoice{created.includes(',') ? 's' : ''}: {created}
                </Alert>
            )}
            {error && <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert>}
            <Card variant="outlined">
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Account</InputLabel>
                                <Select label="Account" value={accountId} onChange={e => setAccountId(e.target.value)}>
                                    {accounts.map(a => (
                                        <MenuItem key={a.accountId} value={a.accountId}>{a.accountName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            {isLoaded ? (
                                <Autocomplete
                                    onLoad={ref => { autocompleteRef.current = ref; }}
                                    onPlaceChanged={onPlaceChanged}
                                    options={{ componentRestrictions: { country: 'us' } }}
                                >
                                    <TextField
                                        fullWidth
                                        label="Pickup address"
                                        placeholder="Start typing and pick from the list"
                                        value={pickup.addressLine}
                                        onChange={e => setPickup({ ...pickup, addressLine: e.target.value, latLong: '' })}
                                    />
                                </Autocomplete>
                            ) : (
                                <TextField fullWidth label="Pickup address" disabled placeholder="Loading address search…" />
                            )}
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField fullWidth label="City" value={pickup.city} InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="State" value={pickup.state} InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth label="Zip" value={pickup.zip} InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Landmark / location notes" value={pickup.landmark}
                                onChange={e => setPickup({ ...pickup, landmark: e.target.value })} />
                        </Grid>
                        {isPrivateProperty && <>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">Vehicle — the plate, or the make, model and color</Typography>
                            </Grid>
                            {[['licensePlate', 'Plate #'], ['vehicleMake', 'Make'], ['vehicleModel', 'Model'], ['vehicleColor', 'Color']].map(([key, label]) => (
                                <Grid item xs={6} sm={3} key={key}>
                                    <TextField fullWidth label={label} value={details[key]} onChange={e => setDetail(key, e.target.value)} />
                                </Grid>
                            ))}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Violation</InputLabel>
                                    <Select label="Violation" value={details.reason} onChange={e => setDetail('reason', e.target.value)}>
                                        {(account.violationOptions || []).map(option => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>}
                        {isCommercial && <>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">See at Pickup</Typography>
                                <Typography variant="caption">Driver with</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Name" value={details.contactName}
                                    disabled={details.noOneWithUnit} onChange={e => setDetail('contactName', e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Number" value={details.contactPhone}
                                    disabled={details.noOneWithUnit} onChange={e => setDetail('contactPhone', e.target.value)} />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">Unattended</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel label="No one with unit" control={
                                    <Checkbox checked={details.noOneWithUnit} onChange={e => setDetail('noOneWithUnit', e.target.checked)} />
                                } />
                            </Grid>
                            {details.noOneWithUnit && (
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Keys location" value={details.keysLocation}
                                        onChange={e => setDetail('keysLocation', e.target.value)} />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">Reference</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth label="PO #" value={details.poNo} onChange={e => setDetail('poNo', e.target.value)} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth label="Reference #" value={details.referenceNo} onChange={e => setDetail('referenceNo', e.target.value)} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth required label="Unit #" value={details.commercialUnit} onChange={e => setDetail('commercialUnit', e.target.value)} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth label="Plate #" value={details.licensePlate} onChange={e => setDetail('licensePlate', e.target.value)} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth multiline minRows={2} label="Defect" value={details.defects}
                                    onChange={e => setDetail('defects', e.target.value)} />
                            </Grid>
                        </>}
                        {(isPrivateProperty || isCommercial) && <>
                            <Grid item xs={12} sm={4}>
                                <FormControlLabel label="Schedule call" control={
                                    <Checkbox checked={details.scheduled} onChange={e => setDetail('scheduled', e.target.checked)} />
                                } />
                            </Grid>
                            {details.scheduled && <>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth type="datetime-local" label="Date & time" InputLabelProps={{ shrink: true }}
                                        value={details.scheduleAt} onChange={e => setDetail('scheduleAt', e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel label="Arrive at" control={
                                        <Checkbox checked={details.scheduleType === 'ARRIVE_AT'} onChange={() => setDetail('scheduleType', 'ARRIVE_AT')} />
                                    } />
                                    <FormControlLabel label="Need by" control={
                                        <Checkbox checked={details.scheduleType === 'NEED_BY'} onChange={() => setDetail('scheduleType', 'NEED_BY')} />
                                    } />
                                </Grid>
                            </>}
                        </>}
                        {contactRequired && !isCommercial && <>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Contact name" value={details.contactName} onChange={e => setDetail('contactName', e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Contact #" value={details.contactPhone} onChange={e => setDetail('contactPhone', e.target.value)} />
                            </Grid>
                        </>}
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth type="number" label="# of vehicles" value={vehicles}
                                inputProps={{ min: 1, max: 10 }}
                                onChange={e => setVehicles(Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1)))} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth type="number" label="# of trucks" value={trucks}
                                inputProps={{ min: 1, max: 10 }}
                                onChange={e => setTrucks(Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1)))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel control={<Checkbox checked={flatbed} onChange={e => setFlatbed(e.target.checked)} />}
                                label="Flatbed needed" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="VIN (optional)" value={vin} inputProps={{ maxLength: 17 }}
                                onChange={e => setVin(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline minRows={3} label="Notes for dispatch" value={note}
                                inputProps={{ maxLength: 1000 }} onChange={e => setNote(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="outlined" component="label">
                                Add vehicle photos
                                <input hidden type="file" accept="image/*" multiple
                                    onChange={e => setPhotos(Array.from(e.target.files || []))} />
                            </Button>
                            {photos.length > 0 && (
                                <Typography component="span" sx={{ marginLeft: 2 }}>
                                    {photos.length} photo{photos.length > 1 ? 's' : ''} selected
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                                {submitting ? 'Submitting...' : 'Submit Request'}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default NewServiceRequest;
